import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
  Typography,
  Stack,
  Chip,
  Alert,
  InputAdornment,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  Close,
  Search,
  Newspaper,
  CalendarToday,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import useFetchDataWithPagination from "../../../utility/useFetchDataWithPagination";
import usePostData from "../../../utility/usePostData";

const BriefsManagement = () => {
  const { isLoading, error, submitData } = usePostData();
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentBrief, setCurrentBrief] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    content: "",
    source: "",
    published_at: new Date().toISOString().split('T')[0],
  });

  const { data, isLoading: loading, paginate, refreshSearch } =
    useFetchDataWithPagination("briefs");
  const [briefsList, setBriefsList] = useState([]);

  useEffect(() => {
    if (data?.data?.data) {
      setBriefsList(data.data.data.data);
    }
  }, [data]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      source: "",
      published_at: new Date().toISOString().split('T')[0],
    });
    setErrorMessage("");
    setEditMode(false);
    setCurrentBrief(null);
  };

  const handleOpenDialog = (brief = null) => {
    if (brief) {
      setEditMode(true);
      setCurrentBrief(brief);
      setFormData({
        title: brief.title || "",
        content: brief.content || "",
        source: brief.source || "",
        published_at: brief.published_at || new Date().toISOString().split('T')[0],
      });
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.title || !formData.content) {
      setErrorMessage("Veuillez remplir tous les champs obligatoires");
      return;
    }

    const endpoint = editMode ? `briefs/${currentBrief.id}` : "briefs";
    const method = editMode ? "PUT" : "POST";

    submitData(endpoint, formData, method);
    refreshSearch();
    handleCloseDialog();
  };

  const handleDelete = (id) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette brève ?")) {
      submitData(`briefs/${id}`, null, "DELETE");
      refreshSearch();
    }
  };

  const filteredBriefs = Array.isArray(briefsList)
    ? briefsList.filter(
        (brief) =>
          brief.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          brief.content.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {isLoading && <LinearProgress color="primary" sx={{ mb: 2 }} />}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 4 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Newspaper color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h4" fontWeight={700} color="primary">
            Gestion des Brèves
          </Typography>
          <Chip label={filteredBriefs.length} color="primary" />
        </Stack>

        <Stack direction="row" spacing={2}>
          <TextField
            size="small"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Nouvelle Brève
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.message}
        </Alert>
      )}

      {loading && <LinearProgress color="secondary" sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        {filteredBriefs.map((brief, index) => (
          <Grid item xs={12} md={6} lg={4} key={index}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 4,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
                    {brief.title}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(brief)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(brief.id)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {brief.content}
                </Typography>

                <Stack spacing={1}>
                  {brief.source && (
                    <Chip
                      label={`Source: ${brief.source}`}
                      size="small"
                      variant="outlined"
                      color="info"
                    />
                  )}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CalendarToday fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(brief.published_at).toLocaleDateString("fr-FR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {filteredBriefs.length === 0 && !loading && (
        <Paper
          elevation={0}
          sx={{
            textAlign: "center",
            py: 8,
            bgcolor: "grey.50",
            borderRadius: 2,
          }}
        >
          <Newspaper sx={{ fontSize: 80, color: "grey.300", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {searchTerm ? "Aucune brève trouvée" : "Aucune brève disponible"}
          </Typography>
          {!searchTerm && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{ mt: 2 }}
            >
              Créer votre première brève
            </Button>
          )}
        </Paper>
      )}

      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        {paginate()}
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">
              {editMode ? "Modifier la brève" : "Nouvelle brève"}
            </Typography>
            <IconButton onClick={handleCloseDialog}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                required
                fullWidth
                label="Titre"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
              />

              <TextField
                required
                fullWidth
                label="Contenu"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                multiline
                rows={4}
              />

              <TextField
                fullWidth
                label="Source (optionnel)"
                name="source"
                value={formData.source}
                onChange={handleInputChange}
                // placeholder="Ex: https://example.com"
              />

              <TextField
                required
                fullWidth
                label="Date de publication"
                name="published_at"
                type="date"
                value={formData.published_at}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {editMode ? "Mettre à jour" : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BriefsManagement;